<template>
	<div class="analytics-container" :class="{noMicro: !!newsletterStore.editingNewsletterId}">
		<div class="right-top-bar">
			<SidebarMicro ref="micro-sidebar" :items="sidebarItems" :base-path="basePath" @switched-item="switchedTab" @switched-child-item="switchedChildTab" />
		</div>
		<div class="content">
			<div class="navbar-container">
				<TopNavbar ref="top-navbar" show-micro-sidebar :base-path="basePath" :sidebar-items="sidebarItems" @switched-item="switchedTab" @switched-child-item="switchedChildTab">
					<template #content>
						<div class="row" v-if="newsletterStore.editingNewsletterId && $route.path.includes('/newsletter')">
							<NewsletterEditNavbar />
						</div>
						<div class="row navbar-slot-content" v-show="loaded" v-else>
							<div class="navbar-left-side">
								<h3>{{ topBarTitle }}</h3>
							</div>
							<div class="navbar-right-side">
								<AppButtonIcon v-if="!newsletterStore.editingNewsletterId && $route.path.includes('/newsletter')"
									class="ml-8"
									icon-name="icon-charts"
									color="var(--color-bg-primary-2)"
									prevent-change
									@click-icon="showAnalytics"
								/>
							</div>
						</div>
					</template>
				</TopNavbar>
			</div>
			<div class="main-content">
				<router-view  />
			</div>
		</div>

		<!-- Newsletter Analytics Bottom Sheet -->
		<AppBottomSheet
			:forceWidth="bottomSheetWidth"
			:active="showAnalyticsBottomSheet"
			@toggling-popup="toggleAnalyticsBottomSheet">
			<NewsletterAnalytics v-if="showAnalyticsBottomSheet" />
		</AppBottomSheet>
	</div>
</template>

<script>
import BusinessesSelect from "@/console-new/components/shared/BusinessesSelect.vue";
import SidebarMicro from "@/console-new/components/shared/SidebarMicro.vue";
import TopNavbar from "@/console-new/components/shared/TopNavbar.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppSelect from "@/shared/components/AppSelect.vue";

import {useLanguageStore} from "@/shared/stores";
import {useUserStore, useRoomsStore, useMarketplaceStore, useAnalyticsStore, useNewsletterStore} from "@/console-new/stores";
import { hasPermissionForRoute, isRouteLockedByPlan, handleLockedRoute } from "@/console-new/helpers/permissions";

import { computed } from 'vue';
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import QromoReport from "@/console-new/components/shared/QromoReport.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import CardSlashed from "@/console-new/components/shared/CardSlashed.vue";
import AppButton from "@/shared/components/AppButton.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import NewsletterEditNavbar from "@/console-new/components/marketing/NewsletterEditNavbar.vue";
import NewsletterAnalytics from "@/console-new/components/marketing/NewsletterAnalytics.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let roomsStore = useRoomsStore();
		let marketplaceStore = useMarketplaceStore();
		let analyticsStore = useAnalyticsStore();
		let newsletterStore = useNewsletterStore();
		return {getString: languageStore.getString, roomsStore, userStore, marketplaceStore, analyticsStore, newsletterStore};
	},
	mixins: [WindowWidth],
	name: "MarketingContainer",
	components: {
		NewsletterEditNavbar,
		NewsletterAnalytics,
		ModalHeader,
		AppButton,
		CardSlashed, QromoReport, AppBottomSheet, AppSelect, BusinessesSelect, SidebarMicro, TopNavbar, AppButtonIcon, AppClickableIcon,
	},
	data() {
		return {
			currentTab: 0,
			currentChildTab: 0,
			loaded: false,
			collapsedSelectors: false,
			showFiltersIcon: false,
			showMobilePreview: false,
			basePath: "/marketing",
			reportQromoActive: false,
			showAnalyticsBottomSheet: false,
		}
	},
	provide() {
		return {
			providedAction: computed(() => {
				return {
					toggleFilters: this.toggleFilters,
					collapsedSelectors: this.collapsedSelectors,
					toggleIconFilters: this.toggleIconFilters,
					showFiltersIcon: this.showFiltersIcon,
					showMobilePreview: this.showMobilePreview,
					toggleMobilePreview: this.toggleMobilePreview,
				}
			})
		}
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		currentSidebarItem() {
			return this.sidebarItems[this.currentTab];
		},
		topBarTitle() {
			let title = this.currentSidebarItem.name;
			if(this.currentSidebarItem.children && this.currentSidebarItem.children[this.currentChildTab]) {
				title += " - "+this.currentSidebarItem.children[this.currentChildTab].name;
			}
			return title;
		},
		sidebarItems() {
			return [
				{
					name: this.getString("newsletter"),
					icon: "iconMail",
					path: "/newsletter",
					shown: hasPermissionForRoute('/marketing/newsletter'),
					locked: isRouteLockedByPlan('/marketing/newsletter'),
					lockedClick: ()=>{handleLockedRoute(this.$router)},
					children: null
				},
				{
					name: this.getString("campaigns"),
					icon: "iconCampaigns",
					path: "/campaigns",
					shown: hasPermissionForRoute('/marketing/campaigns'),
					locked: isRouteLockedByPlan('/marketing/campaigns'),
					lockedClick: ()=>{handleLockedRoute(this.$router)},
					children: null
				},
				{
					name: this.getString("coupons"),
					icon: "iconCoupons",
					path: "/coupons",
					shown: hasPermissionForRoute('/marketing/coupons'),
					locked: isRouteLockedByPlan('/marketing/coupons'),
					lockedClick: ()=>{handleLockedRoute(this.$router)},
					children: null
				}
			]
		}
	},
	methods: {
		toggleFilters() {
			this.collapsedSelectors = !this.collapsedSelectors;
		},
		toggleMobilePreview(force = null) {
			this.showMobilePreview = force !== null ? force : !this.showMobilePreview;
		},
		showAnalytics() {
			this.showAnalyticsBottomSheet = true;
		},
		toggleAnalyticsBottomSheet(isOpen) {
			if (!isOpen) {
				this.showAnalyticsBottomSheet = false;
			}
		},
		switchedTab(index) {
			this.currentTab = index;
		},
		switchedChildTab(childIndex) {
			this.currentChildTab = childIndex;
		},
		updateCurrentTab() {
			let currentRoute = this.$route.path;
			this.sidebarItems.forEach((item, index) => {
				if(currentRoute.endsWith(item.path)) {
					this.currentTab = index;
				} else if(item.children) {
					item.children.forEach((child, childIndex) => {
						if(currentRoute.endsWith(item.path+child.path)) {
							this.currentTab = index;
							this.currentChildTab = childIndex;
						}
					});
				}
			});
		},
	},
	mounted() {
		this.updateCurrentTab();
		this.loaded = true;
	},
}
</script>

<style lang="scss" scoped>
.analytics-container {
	display: flex;
	flex-direction: row;
	height: 100%;


	.right-top-bar {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		width: 250px;
		height: 100%;
		box-shadow: 0 1px 3px var(--color-alpha-3);

		.sidebar-micro {
			@include hideScrollbar;
			overflow-y: auto;
			border-top: 1px solid var(--color-alpha-3);
		}


	}

	.content {
		width: calc(100% - 250px);
	}

	&.noMicro {
		.right-top-bar {
			display: none;
		}
		.content {
			width: 100%;
		}
	}

	.main-content {
		width: 100%;
		height: calc(100% - 60px);
	}

	.navbar-container {
		z-index: 11;
		height: 60px;
		width: 100%;

		.navbar-slot-content {
			align-items: center;
			justify-content: space-between;
			height: 100%;
			padding: 0 10px;

			@media (max-width: 920px) {
				padding: 0 10px 0 2px;
			}

			.navbar-left-side, .navbar-right-side {
				display: flex;
				align-items: center;
				justify-content: flex-start;
				height: 100%;
			}

			.settings-navbar-icon {
				margin-left: 4px;
				width: 15px;
				height: 15px;
				padding: 5px;
				box-sizing: content-box;

				:deep(svg) {
					.fill {
						fill: var(--color-svg-primary);
					}
					.stroke {
						stroke: var(--color-svg-primary);
					}
				}
			}
			@media (max-width: 420px) {
				.settings-navbar-icon {
					margin-left: 2px;
					width: 14px;
					height: 14px;
					padding: 4px;
				}

				.navbar-right-side .base-button {
					min-width: 80px;
				}
			}


			@media (max-width: 350px) {
				.navbar-right-side .base-button {
					min-width: 70px;
					padding: 5px 12px;
				}
			}

		}

	}

	.multi-select-btn {
		display: flex;
		margin-left: auto;
		margin-right: 16px;
	}

	.rotate :deep(.btn-inner img) {
		transition: transform 0.2s ease-in-out;
	}
	.rotate.active :deep(.btn-inner img) {
		transform: rotate(180deg);
	}
	.rotate.inactive :deep(.btn-inner img) {
		transform: rotate(0deg);
	}

	@media (max-width: 1200px) {

		.right-top-bar {
			width: 220px;
		}

		.content {
			width: calc(100% - 220px);
		}
	}

	@media (max-width: 920px) {
		.navbar-container {
			width: 100%;
		}
		.right-top-bar {
			display: none;
		}
		.content {
			width: 100%;
		}
	}

	@media (max-width: 768px) {
		.main-content {
			width: 100%;
		}
	}
}
</style>
